// #region Imports
import { React } from 'Imports';
import { useEffect } from 'react';
import { withStyles, WithStyles, CSSProperties, ThemeProvider, createTheme, StyledEngineProvider } from 'MaterialUIComponents';

import { cx } from '$Utilities/cssHelpers';
import { getLogger } from '$Utilities/logging';
import { ISinglePageApplicationProps } from './spa.types';

import { IConfigServiceInjectedProps, ConfigService } from '$State/ConfigFreezerService';
import { init as initApm } from '@elastic/apm-rum';
import ErrorBoundary from '$Components/Shared/errorBoundary/ErrorBoundary';
import { RouterProvider } from 'react-router-dom';
import { useRouter } from './router/routes';
import favicon = require('./assets/favicon.ico');
// #endregion Imports

// Include the global styles
require('./css/global.scss');

const styles = require('./Application.scss') as {
    active: string;
    link: string;
    logo: string;
    main: string;
    navbar: string;
    page: string;
    pageOuter: string;
    toolbar: string;
};

const theme = createTheme({
    components: {
        MuiTextField: {
            defaultProps: {
                variant: 'standard',
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    backgroundColor: '#E0E0E0',
                    color: 'black',
                    '&:hover': { backgroundColor: '#D5D5D5' },
                },
            },
        },
    },
});

const muiStyles = () => ({
    root: {
        flexGrow: 1,
        height: 430,
        zIndex: 1,
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
    } as CSSProperties,
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    drawerPaper: {
        position: 'relative',
        width: 305,
    } as CSSProperties,
    content: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.default,
        padding: '0 10px',
        minHeight: 0,
        minWidth: 0, // So the Typography noWrap works
    },
    toolbar: theme.mixins.toolbar,
    nested: {
        paddingLeft: theme.spacing(4),
    },
});

const logger = getLogger('Application');

type IApplicationProps = ISinglePageApplicationProps & IConfigServiceInjectedProps & WithStyles<keyof ReturnType<typeof muiStyles>>;

export const _Application: React.FC<IApplicationProps> = (props) => {
    useEffect(() => {
        const initializeComponent = async (): Promise<void> => {
            await props.config.getClientConfig();

            const apm = initApm({
                serviceName: ConfigService.getApmServiceName(),
                serverUrl: ConfigService.getApmServiceUrl(),
                environment: ConfigService.getDeployEnvironment(),
            });
            apm.addLabels({ IntegrationPlatform: ConfigService.getIntegrationPlatform() });
        };

        initializeComponent();
    }, [props.config]);

    const { router } = useRouter({ ...props });

    logger.info('Rendering application');
    props.setFaviconUrl(favicon);

    return (
        <ErrorBoundary>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <div className={cx([props.classes.root, styles.main])}>
                        <main className={props.classes.content} style={{ paddingLeft: 0, paddingRight: 0, minHeight: 0 }}>
                            <div className={props.classes.toolbar} style={{ minHeight: 0 }} />
                            <div className={styles.pageOuter}>
                                <div className={styles.page}>
                                    <RouterProvider router={router} />
                                </div>
                            </div>
                        </main>
                    </div>
                </ThemeProvider>
            </StyledEngineProvider>
        </ErrorBoundary>
    );
};

const Application = withStyles(muiStyles)(ConfigService.inject(_Application));

export { Application, IApplicationProps };
